// Which schema owns a file in the `files` bucket.
//
// `file_classification_cache` is a PER-SCHEMA table, but a storage path is not:
// the schema is encoded in the path itself (`New/Reports/{schema}/...`). Reading
// the cache with an unqualified `supabase.from(...)` therefore uses the
// VIEWER's active schema, which is only right when they happen to be browsing
// their own. With cross-schema providers — and now the fax schema-group
// fan-out — a clinician routinely opens a chart whose files live under a
// different schema, and the unqualified read silently found nothing:
// `.single()` turned that into an HTTP 406 and the UI said "No Extracted Text
// Found" even though the row existed with 13k characters in it.
//
// Mirrors backend `api/ai_assistant._extract_schema_from_path` so both sides
// agree on where a file belongs.

export const PUBLIC_SCHEMA = "public";

/**
 * Schema encoded in a reports path, or "public" when there is no schema
 * segment (the legacy public layout is `New/Reports/{name}_{ehr}/...`).
 *
 * The third segment is the schema unless it looks like a patient folder. A
 * comma means "Last, First"; `Pending` is the unassigned-fax staging folder.
 */
export function schemaFromReportsPath(filePath) {
  const parts = String(filePath || "").split("/");
  if (parts.length >= 5 && parts[0] === "New" && parts[1] === "Reports") {
    const candidate = parts[2];
    if (candidate && !candidate.includes(",") && !candidate.startsWith("Pending")) {
      return candidate;
    }
  }
  return PUBLIC_SCHEMA;
}

/**
 * Schemas to try for a file's cache row, most likely first.
 *
 * The path-derived schema is the answer in every normal case. The viewer's
 * active schema is kept as a fallback because the derivation is a heuristic —
 * a public-schema patient whose folder name happens to contain no comma
 * (e.g. `Forms_535`) reads as a schema name — and one extra miss is far
 * cheaper than telling the user their text doesn't exist.
 */
export function cacheSchemaCandidates(filePath, activeSchema) {
  const fromPath = schemaFromReportsPath(filePath);
  const out = [fromPath];
  const active = String(activeSchema || "").trim();
  if (active && active !== fromPath) out.push(active);
  return out;
}

/**
 * Group paths by the schema that owns them: { [schema]: string[] }.
 * A bulk read has to issue one query PER schema — a single unqualified `.in()`
 * would silently drop every path that lives elsewhere.
 */
export function groupPathsBySchema(paths) {
  const out = {};
  for (const path of paths || []) {
    if (!path) continue;
    const schema = schemaFromReportsPath(path);
    (out[schema] = out[schema] || []).push(path);
  }
  return out;
}

/**
 * A client scoped to `schema`. supabase-js's `.schema()` returns a wrapper, so
 * this is safe to call per query; passing the viewer's own schema returns the
 * client untouched (its default profile is already that schema).
 */
export function clientForSchema(supabase, schema, activeSchema) {
  if (!schema || schema === activeSchema) return supabase;
  return supabase.schema(schema);
}

/**
 * A file's cache row, read from the schema that owns it.
 *
 * Tries cacheSchemaCandidates(filePath, activeSchema), then public (older rows
 * predate the per-schema cache). maybeSingle(), so a miss in one schema is an
 * empty result, not an HTTP 406. Returns { data, error, schema }: `schema` is
 * where the row was found, so a caller that writes back can write THERE --
 * an unqualified upsert lands in the viewer's active schema and leaves a stray
 * duplicate row instead of changing the real one.
 */
export async function readCacheRow(supabase, filePath, columns, activeSchema) {
  const schemas = cacheSchemaCandidates(filePath, activeSchema);
  if (!schemas.includes(PUBLIC_SCHEMA)) schemas.push(PUBLIC_SCHEMA);
  let lastError = null;
  for (const schema of schemas) {
    const { data, error } = await clientForSchema(supabase, schema, activeSchema)
      .from("file_classification_cache")
      .select(columns)
      .eq("file_path", filePath)
      .maybeSingle();
    if (data) return { data, error: null, schema };
    if (error) lastError = error;
  }
  return { data: null, error: lastError, schema: null };
}
